import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { GuideService } from '../../../services/resources/guide.service';
import { GuideDetailsComponent } from './guide-details/guide-details.component';
import { GuideDetailInterface, Scope } from '../../../core/models';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
} from '@angular/forms';
import { formatUnixToTimeString } from '../../../core/booking-from.utils';
import { Timestamp } from '@angular/fire/firestore';
import { first, Observable, shareReplay, tap } from 'rxjs';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { TourDetailService } from '../../../services/resources/tour-detail.service';
import { LoadingBarrierService } from '../../../services/resources/loading-barrier.service';

@Component({
  selector: 'app-guides',
  standalone: true,
  imports: [CommonModule, GuideDetailsComponent, FormsModule, DragDropModule],
  template: `
    <div *ngIf="guides$ | async as guidesData">
      <div *ngIf="tours$ | async as tours">
        <div
          class="flex flex-col sm:flex-row sm:items-end sm:justify-center gap-y-3 sm:gap-y-0 pb-4 w-full"
        >
          <div class="flex flex-col sm:pr-6">
            <h2 class="text-base font-semibold leading-7 text-gray-900">
              Guides
            </h2>
            <p class="mt-1 text-sm leading-6 text-gray-600">
              Bitte Guides zweisen:
            </p>
          </div>
          <div class="w-full sm:w-auto">
            <!-- Combobox -->
            <div class="relative">
              <label for="guide-combobox" class="sr-only">Guide suchen</label>
              <div class="relative">
                <input
                  #comboInput
                  id="guide-combobox"
                  type="text"
                  [value]="searchQuery"
                  (input)="onSearch($event, guidesData)"
                  (focus)="onFocus(guidesData)"
                  autocomplete="off"
                  placeholder="Guide suchen..."
                  class="text-sm sm:text-xs rounded-md border border-gray-300 py-2 px-3 pr-8 text-gray-700 focus:border-columbus-blue-500 focus:outline-none focus:ring-1 focus:ring-columbus-blue-500 w-full sm:w-52"
                />
                <button
                  type="button"
                  (click)="toggleMenu(guidesData)"
                  class="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>

                <ul
                  *ngIf="isMenuOpen && filteredGuides.length > 0"
                  class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-xs"
                  role="listbox"
                >
                  <li
                    *ngFor="let guide of filteredGuides"
                    (click)="selectGuide(guide)"
                    (keydown.enter)="selectGuide(guide)"
                    role="option"
                    tabindex="0"
                    class="hover:text-white hover:bg-columbus-blue-600 active:bg-columbus-blue-700 active:text-white relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900"
                  >
                    <span class="block">
                      {{ guide.surname }} {{ guide.forename }}
                    </span>
                    <span
                      *ngIf="selectedGuide?.id === guide.id"
                      class="absolute inset-y-0 right-0 flex items-center pr-4 text-columbus-blue-600"
                    >
                      <svg
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="sm:pl-6 w-full sm:w-auto">
            <button
              type="button"
              [disabled]="!selectedGuide"
              (click)="addGuide(selectedGuide)"
              class="w-full sm:w-auto rounded-md disabled:opacity-25 bg-columbus-blue-600 px-3 py-2 sm:px-2.5 sm:py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
            >
              Hinzufügen
            </button>
          </div>
        </div>
        <div
          cdkDropList
          [cdkDropListAutoScrollDisabled]="true"
          [cdkDropListData]="guides.controls"
          (cdkDropListDropped)="drop($event)"
          *ngIf="guides && guides.length > 0"
          class="flex flex-col gap-2"
          style="touch-action: auto;"
        >
          <div
            *ngFor="let guide of guides.controls; let i = index"
            cdkDrag
            class="flex flex-col justify-center items-stretch p-3 bg-white border border-gray-200 rounded-lg w-full"
          >
            <button
              cdkDragHandle
              type="button"
              class="self-center rounded-md bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mb-2 cursor-grab active:cursor-grabbing"
            >
              <svg
                class="size-6 rotate-90"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M8.5 7C9.32843 7 10 6.32843 10 5.5C10 4.67157 9.32843 4 8.5 4C7.67157 4 7 4.67157 7 5.5C7 6.32843 7.67157 7 8.5 7ZM8.5 13.5C9.32843 13.5 10 12.8284 10 12C10 11.1716 9.32843 10.5 8.5 10.5C7.67157 10.5 7 11.1716 7 12C7 12.8284 7.67157 13.5 8.5 13.5ZM10 18.5C10 19.3284 9.32843 20 8.5 20C7.67157 20 7 19.3284 7 18.5C7 17.6716 7.67157 17 8.5 17C9.32843 17 10 17.6716 10 18.5ZM15.5 7C16.3284 7 17 6.32843 17 5.5C17 4.67157 16.3284 4 15.5 4C14.6716 4 14 4.67157 14 5.5C14 6.32843 14.6716 7 15.5 7ZM17 12C17 12.8284 16.3284 13.5 15.5 13.5C14.6716 13.5 14 12.8284 14 12C14 11.1716 14.6716 10.5 15.5 10.5C16.3284 10.5 17 11.1716 17 12ZM15.5 20C16.3284 20 17 19.3284 17 18.5C17 17.6716 16.3284 17 15.5 17C14.6716 17 14 17.6716 14 18.5C14 19.3284 14.6716 20 15.5 20Z"
                ></path>
              </svg>
            </button>

            <!-- Duration -->
            <app-guide-details
              (delete)="onDelete($event, i)"
              [guide]="convertAbstractControl(guide)"
              [tours]="tours"
            />
          </div>
        </div>
      </div>
    </div>
  `,
  styles: ``,
})
export class GuidesComponent implements OnInit {
  @Input({ required: true }) guides!: FormArray<FormGroup>;
  @Input({ required: true }) startTime!: Timestamp;
  guideService = inject(GuideService);
  tourService = inject(TourDetailService);
  fb = inject(FormBuilder);

  private readonly barrier = inject(LoadingBarrierService);

  guides$: Observable<GuideDetailInterface[]> | undefined = undefined;
  tours$ = this.tourService.getTours();

  editorScope: Scope = 'editor';

  isMenuOpen = false;
  searchQuery = '';
  selectedGuide: GuideDetailInterface | null = null;
  filteredGuides: GuideDetailInterface[] = [];

  ngOnInit(): void {
    this.regBarrierOnLoad();
    const source$ = this.guideService
      .getGuides()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.guides$ = source$.pipe(
      first(),
      tap(() => this.setBarrierOnLoad()),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  regBarrierOnLoad() {
    if (!(this.guides.length > 0)) {
      this.barrier.register(this.editorScope, 'guides-source');
    }
  }
  setBarrierOnLoad() {
    if (!(this.guides.length > 0)) {
      this.barrier.markReady(this.editorScope, 'guides-source');
    }
  }

  drop(event: CdkDragDrop<FormGroup[]>): void {
    // 1. Reorder the FormArray controls
    const control = this.guides.at(event.previousIndex);
    this.guides.removeAt(event.previousIndex);
    this.guides.insert(event.currentIndex, control);

    // 2. (optional) If you need to trigger validators or valueChanges:
    this.guides.updateValueAndValidity();
  }

  convertAbstractControl(aControl: AbstractControl) {
    return aControl as FormGroup;
  }

  addGuide(guide: GuideDetailInterface | null) {
    if (!guide) return;
    const guideForm = this.createGuide(guide);
    if (guideForm) {
      this.guides.push(guideForm);
      this.selectedGuide = null;
      this.searchQuery = '';
    }
  }

  createGuide(guide: GuideDetailInterface): FormGroup {
    const guideForm = this.fb.group({
      guideDetails: this.fb.group({
        id: [''],
        forename: [''],
        surname: [''],
        tel: [''],
        email: [''],
        languages: this.fb.array([]),
      }),
      positionDetails: this.fb.group({
        id: [''],
        time: [this.calculatePositionTime(this.startTime)],
        location: [''],
      }),
      tour: this.fb.group({
        id: [''],
        tourInfo: this.fb.group({
          id: [''],
          tourName: [''],
          colorCode: [''],
        }),
        tourConfirm: [false],
        tourLanguage: [''],
      }),
      storno: this.fb.group({
        customStorno: [''],
        storno: [''],
        isActive: [false],
      }),
      other: this.fb.array([]),
    });

    const guideDetails = guideForm.get('guideDetails') as FormGroup;
    guideDetails.patchValue({
      id: guide.id,
      forename: guide.forename,
      surname: guide.surname,
      tel: guide.tel,
      email: guide.email,
    });

    const languagesArray = guideDetails.get('languages') as FormArray;
    Object.entries(guide.languages).forEach(([language]) => {
      languagesArray.push(
        this.fb.group({
          name: [language],
          isActive: [false],
        }),
      );
    });

    return guideForm;
  }

  onDelete(value: boolean, index: number) {
    if (value) {
      this.guides.removeAt(index);
    }
  }

  calculatePositionTime(time: Timestamp): string {
    const calendarSeconds = time.seconds;
    const adjustedSeconds = calendarSeconds - 15 * 60;
    return formatUnixToTimeString(adjustedSeconds);
  }

  updateTime(time: Timestamp) {
    if (this.guides.length > 0) {
      this.guides.controls.forEach((guideControl) => {
        this.positionTimeControl(guideControl).patchValue(
          this.calculatePositionTime(time),
        );
      });
    }
  }

  positionTimeControl(control: AbstractControl) {
    return (control.get('positionDetails') as FormGroup).get(
      'time',
    ) as FormControl;
  }

  trackByIndex(i: number): number {
    return i;
  }

  onSearch(event: Event, allGuides: GuideDetailInterface[]) {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery = query;
    this.selectedGuide = null;
    this.filteredGuides = this.filterGuides(allGuides, query);
    this.isMenuOpen = true;
  }

  onFocus(allGuides: GuideDetailInterface[]) {
    this.filteredGuides = this.filterGuides(allGuides, this.searchQuery);
    this.isMenuOpen = true;
  }

  toggleMenu(allGuides: GuideDetailInterface[]) {
    this.filteredGuides = this.filterGuides(allGuides, this.searchQuery);
    this.isMenuOpen = !this.isMenuOpen;
  }

  filterGuides(
    allGuides: GuideDetailInterface[],
    query: string,
  ): GuideDetailInterface[] {
    if (!query) {
      return [...allGuides].sort((a, b) => a.surname.localeCompare(b.surname));
    }
    const q = query.toLowerCase();
    return allGuides
      .filter(
        (g) =>
          g.surname.toLowerCase().includes(q) ||
          g.forename.toLowerCase().includes(q),
      )
      .sort((a, b) => a.surname.localeCompare(b.surname));
  }

  selectGuide(guide: GuideDetailInterface) {
    this.selectedGuide = guide;
    this.searchQuery = `${guide.surname} ${guide.forename}`;
    this.isMenuOpen = false;
  }
}
